/**
 * authErrors — turn raw Cognito / amazon-cognito-identity-js failures into
 * specific, actionable copy for the sign-in and password-recovery pages.
 *
 * Pure module (no React, no Cognito import) so it can be unit-tested and so
 * the pages never have to string-match Cognito's messages themselves.
 *
 * Background (Dev Handoff 2026-09-07, `vtt-auth-signin-password-reset`):
 * players were shown Cognito's raw text — "User password cannot be reset in
 * the current state.", "Temporary password has expired and must be reset by
 * an administrator." — and concluded they needed a *new account*. Every
 * branch below says what happened, what to do next, and whether the DM has
 * to be involved, and never suggests creating an account.
 */

/** Which flow produced the error — the same Cognito code means different things per flow. */
export type AuthFlow = 'signIn' | 'newPassword' | 'requestReset' | 'confirmReset' | 'session';

/** A follow-up the UI can offer alongside the message. */
export type AuthErrorAction =
  /** Link to the forgot-password flow (prefilled with the email). */
  | 'forgotPassword'
  /** Re-send the code (reset code or confirmation code). */
  | 'resendCode'
  /** Nothing the player can do alone — the DM must act from the Admin page. */
  | 'contactDm'
  /** Transient — try the same thing again. */
  | 'retry'
  /** Go back to the sign-in form. */
  | 'signIn'
  /** Jump straight into the reset flow and send a code. */
  | 'goToReset';

export interface AuthErrorInfo {
  /** Normalised Cognito error code (e.g. "NotAuthorizedException"), "NetworkError", or "Unknown". */
  code: string;
  /** Short headline, sentence case, no trailing period. */
  title: string;
  /** One or two sentences: what happened and what to do next. */
  detail: string;
  action?: AuthErrorAction;
}

/** Contact line appended wherever the DM has to act. Kept in one place so it reads the same everywhere. */
export const DM_CONTACT_HINT =
  'Message the DM — they can fix it from the Admin page in under a minute.';

/**
 * Cognito's default temporary-password lifetime. The pool setting isn't
 * readable from the browser; we quote the default and the DM checklist
 * asks them to confirm it.
 */
export const TEMP_PASSWORD_DAYS = 7;

interface ErrorLike {
  code?: unknown;
  name?: unknown;
  message?: unknown;
}

/** Extract the Cognito error code from whatever the SDK threw. */
export function getAuthErrorCode(err: unknown): string {
  if (!err || typeof err !== 'object') return 'Unknown';
  const e = err as ErrorLike;
  const code = typeof e.code === 'string' && e.code ? e.code : typeof e.name === 'string' ? e.name : '';
  const message = typeof e.message === 'string' ? e.message : '';
  if (code === 'NetworkError' || /network error/i.test(message) || code === 'TypeError') {
    return 'NetworkError';
  }
  if (!code || code === 'Error') {
    // Library-generated errors (no service code) — classify by message.
    if (/missing an id token|please authenticate|username is null/i.test(message)) {
      return 'NoSession';
    }
    return 'Unknown';
  }
  return code;
}

export function getAuthErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && typeof (err as ErrorLike).message === 'string') {
    return (err as ErrorLike).message as string;
  }
  return typeof err === 'string' ? err : '';
}

/**
 * Trim and lowercase an email typed by a player. Phone keyboards
 * auto-capitalise the first letter and append a space after
 * autocomplete; both make Cognito answer "user does not exist".
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Keep only the digits of a pasted verification code ("123 456" → "123456"). */
export function normalizeCode(raw: string): string {
  return raw.replace(/\D+/g, '');
}

/**
 * Cognito reports policy failures as
 * "Password did not conform with policy: Password must have uppercase characters".
 * Strip the prefix so the player sees just the rule that failed.
 */
export function describePasswordPolicyFailure(message: string): string {
  const stripped = message.replace(/^password did not conform with policy:\s*/i, '').trim();
  if (!stripped) {
    return 'Use at least 8 characters with upper and lower case letters, a number and a symbol.';
  }
  return stripped.endsWith('.') ? stripped : `${stripped}.`;
}

const TOO_MANY: AuthErrorInfo = {
  code: 'LimitExceededException',
  title: 'Too many attempts',
  detail: 'Cognito has paused this account for a short while. Wait about 15 minutes, then try again.',
  action: 'retry',
};

function network(code: string): AuthErrorInfo {
  return {
    code,
    title: "Can't reach the sign-in service",
    detail: 'Check your internet connection and try again. Nothing is wrong with your account.',
    action: 'retry',
  };
}

function misconfigured(code: string, message: string): AuthErrorInfo {
  return {
    code,
    title: 'Sign-in is misconfigured',
    detail: `The app could not talk to its user pool (${message || code}). This is not your account — tell the DM.`,
    action: 'contactDm',
  };
}

function unknown(flow: AuthFlow, code: string, message: string): AuthErrorInfo {
  const what =
    flow === 'signIn'
      ? 'Sign-in failed'
      : flow === 'newPassword'
        ? 'Could not set your password'
        : flow === 'requestReset'
          ? 'Could not send a reset code'
          : flow === 'confirmReset'
            ? 'Could not reset your password'
            : 'Session problem';
  return {
    code,
    title: what,
    detail: `${message ? `${message.replace(/\.?$/, '.')} ` : ''}If this keeps happening, tell the DM — you do not need a new account.`,
    action: 'contactDm',
  };
}

const NO_ACCOUNT: Omit<AuthErrorInfo, 'code'> = {
  title: 'No account for that email',
  detail:
    'Use the exact address the DM invited you with. If you are not sure which one that was, ask the DM — do not create a new account.',
  action: 'contactDm',
};

const FIRST_SIGN_IN_INCOMPLETE: Omit<AuthErrorInfo, 'code'> = {
  title: 'Finish your first sign-in instead',
  detail:
    'This account has never completed its first sign-in, so Cognito will not email a reset code. ' +
    'Sign in with the temporary password from your invite email and you will be asked to choose a permanent one. ' +
    `If that email is gone or older than ${TEMP_PASSWORD_DAYS} days, the DM can resend the invite or give you a new temporary password. ` +
    DM_CONTACT_HINT,
  action: 'contactDm',
};

/**
 * Map a thrown error to player-facing copy for the given flow.
 * Never returns Cognito's raw text on its own; unknown errors get the raw
 * message plus a "tell the DM" line.
 */
export function describeAuthError(err: unknown, flow: AuthFlow): AuthErrorInfo {
  const code = getAuthErrorCode(err);
  const message = getAuthErrorMessage(err);

  if (code === 'NetworkError') return network(code);
  if (code === 'TooManyRequestsException' || code === 'LimitExceededException') {
    return { ...TOO_MANY, code };
  }
  if (code === 'ResourceNotFoundException' || code === 'InvalidUserPoolConfigurationException') {
    return misconfigured(code, message);
  }
  if (code === 'UserNotFoundException') return { code, ...NO_ACCOUNT };
  if (code === 'InvalidPasswordException') {
    return {
      code,
      title: 'That password does not meet the rules',
      detail: describePasswordPolicyFailure(message),
    };
  }

  switch (flow) {
    case 'signIn':
      return describeSignInError(code, message);
    case 'newPassword':
      return describeNewPasswordError(code, message);
    case 'requestReset':
      return describeRequestResetError(code, message);
    case 'confirmReset':
      return describeConfirmResetError(code, message);
    case 'session':
      return describeSessionError(code);
    default:
      return unknown(flow, code, message);
  }
}

function describeSignInError(code: string, message: string): AuthErrorInfo {
  switch (code) {
    case 'NotAuthorizedException': {
      if (/temporary password has expired/i.test(message)) {
        return {
          code,
          title: 'Your temporary password has expired',
          detail:
            `Invite passwords only work for ${TEMP_PASSWORD_DAYS} days. Your account still exists — ` +
            'the DM just needs to resend your invite or give you a new temporary password. ' +
            DM_CONTACT_HINT,
          action: 'contactDm',
        };
      }
      if (/password attempts exceeded/i.test(message)) {
        return {
          code,
          title: 'Too many wrong passwords',
          detail:
            'Cognito has locked sign-in for a few minutes. Wait, then try again — or reset your password now and skip the wait.',
          action: 'forgotPassword',
        };
      }
      if (/user is disabled/i.test(message)) {
        return {
          code,
          title: 'This account is disabled',
          detail: `The DM has disabled this account. ${DM_CONTACT_HINT}`,
          action: 'contactDm',
        };
      }
      return {
        code,
        title: 'Wrong email or password',
        detail:
          'Check for typos and caps lock. If you cannot remember your password, reset it — a code is emailed to you and it takes about a minute.',
        action: 'forgotPassword',
      };
    }
    case 'UserNotConfirmedException':
      return {
        code,
        title: 'Account not confirmed yet',
        detail:
          'This account was created but never confirmed. We can email you a new confirmation code, or the DM can confirm it from the Admin page.',
        action: 'resendCode',
      };
    case 'PasswordResetRequiredException':
      return {
        code,
        title: 'A password reset is waiting for you',
        detail:
          'The DM reset your password. We will email you a code — enter it and choose a new password, then sign in.',
        action: 'goToReset',
      };
    case 'InvalidParameterException':
      return {
        code,
        title: 'Sign-in failed',
        detail: `${message.replace(/\.?$/, '.')} Check the email address and try again.`,
        action: 'retry',
      };
    default:
      return unknown('signIn', code, message);
  }
}

function describeNewPasswordError(code: string, message: string): AuthErrorInfo {
  switch (code) {
    case 'NotAuthorizedException':
      if (/session is expired|invalid session/i.test(message)) {
        return {
          code,
          title: 'That took a little too long',
          detail:
            'The first-sign-in step timed out. Sign in again with your temporary password and choose your new one straight away.',
          action: 'signIn',
        };
      }
      return unknown('newPassword', code, message);
    case 'InvalidParameterException':
      return {
        code,
        title: 'Could not set your password',
        detail: `${message.replace(/\.?$/, '.')} Sign in again with your temporary password and retry. If it repeats, tell the DM.`,
        action: 'signIn',
      };
    default:
      return unknown('newPassword', code, message);
  }
}

function describeRequestResetError(code: string, message: string): AuthErrorInfo {
  switch (code) {
    case 'NotAuthorizedException':
      if (/cannot be reset in the current state/i.test(message)) {
        return { code, ...FIRST_SIGN_IN_INCOMPLETE };
      }
      if (/user is disabled/i.test(message)) {
        return {
          code,
          title: 'This account is disabled',
          detail: `The DM has disabled this account. ${DM_CONTACT_HINT}`,
          action: 'contactDm',
        };
      }
      return unknown('requestReset', code, message);
    case 'UserNotConfirmedException':
      return { code, ...FIRST_SIGN_IN_INCOMPLETE };
    case 'InvalidParameterException':
      if (/no registered\/verified email|verified email|verified phone/i.test(message)) {
        return {
          code,
          title: 'No verified email on this account',
          detail:
            'Cognito will not email a code because the address on your account is not marked as verified. ' +
            'The DM can fix that with one click (Verify email on the Admin page) — then send the code again. ' +
            DM_CONTACT_HINT,
          action: 'contactDm',
        };
      }
      return {
        code,
        title: 'Could not send a reset code',
        detail: `${message.replace(/\.?$/, '.')} Check the email address and try again.`,
        action: 'retry',
      };
    case 'CodeDeliveryFailureException':
      return {
        code,
        title: 'The email could not be sent',
        detail: `Cognito failed to deliver the code. ${DM_CONTACT_HINT}`,
        action: 'contactDm',
      };
    default:
      return unknown('requestReset', code, message);
  }
}

function describeConfirmResetError(code: string, message: string): AuthErrorInfo {
  switch (code) {
    case 'CodeMismatchException':
      return {
        code,
        title: 'That code is not right',
        detail:
          'Check the 6-digit code in the email. If you requested more than one, only the newest code works — or send a fresh one.',
        action: 'resendCode',
      };
    case 'ExpiredCodeException':
      return {
        code,
        title: 'That code has expired',
        detail: 'Codes only last about an hour. Send a new one and use it straight away.',
        action: 'resendCode',
      };
    case 'NotAuthorizedException':
      if (/cannot be reset in the current state/i.test(message)) {
        return { code, ...FIRST_SIGN_IN_INCOMPLETE };
      }
      return unknown('confirmReset', code, message);
    case 'InvalidParameterException':
      return {
        code,
        title: 'Could not reset your password',
        detail: `${message.replace(/\.?$/, '.')} Check the code and password, then try again.`,
        action: 'retry',
      };
    default:
      return unknown('confirmReset', code, message);
  }
}

/** Session-refresh failures are never shown raw — only the code matters here. */
function describeSessionError(code: string): AuthErrorInfo {
  if (code === 'NoSession') {
    return {
      code,
      title: 'Signed out',
      detail: 'Sign in to continue.',
      action: 'signIn',
    };
  }
  return {
    code,
    title: 'Your session expired',
    detail: 'Sign in again to pick up where you left off — your character and scenes are unchanged.',
    action: 'signIn',
  };
}
